import type { ReactNode } from 'react';
import { useState } from 'react';
import { Stack } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { getSelectedPlanId } from '@utils/cartUtils';
import { PlanDetailsModal } from '@components/ProductOffers/modals/PlanDetailsModal';
import { SwitchPlanModal } from '@components/ProductOffers/modals/SwitchPlanModal';
import { InternetRequiredModal } from '@components/ProductOffers/modals/InternetRequiredModal';
import type { ShoppingCartDTO } from '@t/api.types';
import type { CategoryType, Plan } from '@t/productOffering.types';
import { PlanCard } from '../PlanCard';

interface PlansSectionProps {
  title: string;
  plans: Plan[];
  cart: ShoppingCartDTO | null;
  categoryType: CategoryType;
  categoryLabel: string;
  backgroundColor: string;
  isFirst?: boolean;
  isLast?: boolean;
  hasInternet?: boolean;
  isSelectDisabled?: boolean;
  isCartMutating?: boolean;
  pendingPlanId?: string | null;
  onPlanSelected: (plan: Plan) => void;
  onEditPlan?: (plan: Plan) => void;
  onSwitchPlan: (oldOfferingId: string, newPlan: Plan) => void;
  renderCardChildren?: (plan: Plan) => ReactNode;
}

export const PlansSection = ({
  title,
  plans,
  cart,
  categoryType,
  categoryLabel,
  backgroundColor,
  isFirst,
  isLast,
  hasInternet,
  isSelectDisabled,
  isCartMutating,
  pendingPlanId,
  onPlanSelected,
  onEditPlan,
  onSwitchPlan,
  renderCardChildren,
}: PlansSectionProps) => {
  const [detailsPlan, setDetailsPlan] = useState<Plan | null>(null);
  const [pendingSwitchPlan, setPendingSwitchPlan] = useState<Plan | null>(null);
  const [internetRequiredOpen, setInternetRequiredOpen] = useState(false);

  const selectedPlanId = getSelectedPlanId(cart, plans);
  const requiresInternet = hasInternet !== undefined;

  const bandPositionClass =
    [isFirst && 'plan-section-band--first', isLast && 'plan-section-band--last'].filter(Boolean).join(' ') ||
    undefined;

  const handleSelectPlan = (plan: Plan) => {
    if (requiresInternet && !hasInternet) {
      setInternetRequiredOpen(true);
      return;
    }
    if (selectedPlanId && selectedPlanId !== plan.planId) {
      setPendingSwitchPlan(plan);
      return;
    }
    onPlanSelected(plan);
  };

  const handleConfirmSwitch = () => {
    if (!pendingSwitchPlan || !selectedPlanId) return;
    onSwitchPlan(selectedPlanId, pendingSwitchPlan);
    setPendingSwitchPlan(null);
  };

  return (
    <Paper variant="planSectionBand" className={bandPositionClass} sx={{ backgroundColor }}>
      <Container>
        <Stack spacing={4.5} useFlexGap>
          <Typography variant="heading.section" className="PlansSection-heading">
            {title}
          </Typography>

          <Box
            sx={(theme: Theme) => ({
              display: 'grid',
              // One column on phones; from tablet up, fit as many >=330px cards
              // per row as the width allows (2 then 3) so a card is never skinny.
              // A single-plan section stays one full-width card.
              gridTemplateColumns: {
                xs: theme.novus.planGridColumns.xs,
                md: plans.length > 1 ? theme.novus.planGridColumns.md : '1fr',
              },
              gap: theme.novus.planGridGap,
            })}
          >
            {plans.map((plan) => (
              <PlanCard
                key={plan.planId}
                plan={plan}
                isSelected={selectedPlanId === plan.planId}
                isSelectDisabled={isSelectDisabled}
                onSelectPlan={() => handleSelectPlan(plan)}
                onEdit={onEditPlan && selectedPlanId === plan.planId ? () => onEditPlan(plan) : undefined}
                onViewDetails={() => setDetailsPlan(plan)}
                categoryType={categoryType}
                isSinglePlan={plans.length === 1}
                loading={pendingPlanId === plan.planId}
              >
                {renderCardChildren?.(plan)}
              </PlanCard>
            ))}
          </Box>
        </Stack>
      </Container>

      <PlanDetailsModal
        open={!!detailsPlan}
        onClose={() => setDetailsPlan(null)}
        plan={detailsPlan}
        categoryType={categoryType}
      />

      <SwitchPlanModal
        open={!!pendingSwitchPlan}
        onClose={() => setPendingSwitchPlan(null)}
        onConfirm={handleConfirmSwitch}
        categoryLabel={categoryLabel}
        loading={isCartMutating}
      />

      {requiresInternet && (
        <InternetRequiredModal
          open={internetRequiredOpen}
          onClose={() => setInternetRequiredOpen(false)}
          categoryLabel={categoryLabel}
        />
      )}
    </Paper>
  );
};
